import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Smile, History as HistoryIcon, Loader2, Check, X as XIcon, AlertTriangle, Palette, Eraser, ClipboardList, Stethoscope, Undo2, Redo2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import useMediaQuery from "@/lib/useMediaQuery";
import { formatDoctorName } from "@/lib/doctorName";
import ToothChart, {
  canRenderFullArchAtWidth,
  getFullArchMaximumCanvasWidth,
  getFullArchMinimumCanvasWidth,
} from "./ToothChart";
import ChartFindingsLegend from "./ChartFindingsLegend";
import ChartedFindingsSummaryPanel from "./ChartedFindingsSummaryPanel";
import FindingsPalette, { buildPaletteCodeGroups, flattenPaletteFindings } from "./FindingsPalette";
import { PULP_OUTLINE_COLOR } from "./findingPalette.config";
import MobilePaletteSheet from "./MobilePaletteSheet";
import MobileProcedurePaletteSheet from "./MobileProcedurePaletteSheet";
import ToothNoteDialog from "./ToothNoteDialog";
import CustomFindingDialog from "./CustomFindingDialog";
import ChairsideModeToggle from "./ChairsideModeToggle";
import TreatmentTab from "@/components/treatment/TreatmentTab";
import { buildBridgeFindingGroups } from "./bridgeGroups";
import { normalizeChartFinding } from "./chartFindingBehavior";
import {
  applyCompletedWorkChartEffects,
  buildCompletedWorkByTooth,
} from "./completedWorkChartState";
import {
  EMPTY_STATE,
  applySurfaceFindingWithAutoFlags,
  applyToothFinding,
  eraseSurface,
  clearTooth,
  removeToothFinding,
  setNote,
  getNoteText,
} from "./chartState";
import { defaultDentitionFromDOB } from "./dentition";

export const SIDE_VIEW_BREAKPOINT = getFullArchMinimumCanvasWidth();
// Horizontal padding the chart card puts around the odontogram canvas at its
// widest padding step (`sm:p-6` → 24px a side).
export const CHART_CARD_HORIZONTAL_PADDING_PX = 48;
// The chart column stops being useful once every tooth has hit its 64px
// ceiling — beyond this the arch just floats in the middle of a wider and
// wider card. Capping the column here is what frees the surplus for the rail.
export const CHART_CONTENT_MAX_WIDTH_PX =
  getFullArchMaximumCanvasWidth() + CHART_CARD_HORIZONTAL_PADDING_PX;
export const PROCEDURE_RAIL_MIN_WIDTH_PX = 340;
// The rail's guaranteed share: a third of the row, floored at 340px (so the
// two-column pen buttons never crush) and ceilinged at 440px. This is the
// width the rail has always had, and it never drops below it.
export const PROCEDURE_RAIL_BASE_MAX_WIDTH_PX = 440;
// Once the odontogram saturates, the rail keeps absorbing the surplus past
// its guaranteed share up to 640px, so it never balloons on huge monitors.
export const PROCEDURE_RAIL_MAX_WIDTH_PX = 640;
export const CHART_CARD_CLASSNAME = "bg-white rounded-2xl shadow-sm p-2 min-[360px]:p-4 sm:p-6 overflow-hidden min-w-0 relative";
export const IPAD_11_LANDSCAPE_WIDTH_PX = 1194;
export const IPAD_12_9_LANDSCAPE_WIDTH_PX = 1366;
// Palette docks as the side rail from tablet-landscape width up (900px),
// matching the `isDesktop` line the rest of this component already uses.
// Below 900px — phones and portrait-held small tablets — the rail hides
// and the floating "Findings" chip opens the palette as a bottom sheet.
export const PALETTE_RAIL_BREAKPOINT_PX = 900;
// Matches the `gap-4` on the chart body grids below.
export const CHART_BODY_GRID_GAP_PX = 16;
export const CHART_BODY_GRID_CLASSNAME = "grid gap-4";
// Let the chart and the module row consume the full Doctor workspace on
// wide monitors. The procedure rail keeps its own 440px ceiling, while
// the primary chart column and the cards below absorb any extra width.
export const CHART_BODY_DOCKED_GRID_CLASSNAME = "grid w-full gap-4";
export const CHART_BELOW_GRID_CLASSNAME = "grid w-full gap-4 mt-3 items-stretch min-[900px]:grid-cols-[3fr_2fr]";
export const CHART_BELOW_CELL_CLASSNAME = "min-w-0 [&>*]:h-full";
export const PALETTE_RAIL_CLASSNAME = "hidden min-[900px]:block min-[900px]:sticky min-[900px]:top-4 min-[900px]:self-start";
export const PALETTE_OVERRIDE_RAIL_CLASSNAME = "hidden min-[900px]:block min-[900px]:sticky min-[900px]:top-4 min-[900px]:self-start";
export const FLOATING_PALETTE_CHIP_CLASSNAME = "min-[900px]:hidden fixed right-4 bottom-[max(env(safe-area-inset-bottom),1rem)] z-30 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-2xl active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2";

export function isPaletteRailDockedViewport(viewportWidth) {
  return Number.isFinite(viewportWidth) && viewportWidth >= PALETTE_RAIL_BREAKPOINT_PX;
}

export function getChartBodyGridClassName(hasDockedProcedureRail) {
  return hasDockedProcedureRail
    ? CHART_BODY_DOCKED_GRID_CLASSNAME
    : CHART_BODY_GRID_CLASSNAME;
}

export function getBelowChartEqualColumnsStyle({
  hasDockedProcedureRail,
  equalColumns,
  hasLeftContent,
  hasRightContent,
} = {}) {
  if (!hasDockedProcedureRail || !equalColumns || !hasLeftContent || !hasRightContent) return undefined;
  return { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" };
}

export function getProcedureModeGridStyle(hasProcedureRail, { dockPaletteRail = true } = {}) {
  if (!hasProcedureRail || !dockPaletteRail) return undefined;
  return {
    "--chart-content-max-width": `${CHART_CONTENT_MAX_WIDTH_PX}px`,
    "--procedure-rail-min-width": `${PROCEDURE_RAIL_MIN_WIDTH_PX}px`,
    "--procedure-rail-base-max-width": `${PROCEDURE_RAIL_BASE_MAX_WIDTH_PX}px`,
    "--procedure-rail-max-width": `${PROCEDURE_RAIL_MAX_WIDTH_PX}px`,
    "--chart-body-grid-gap": `${CHART_BODY_GRID_GAP_PX}px`,
  };
}

// Row width is handed out in three stages, and the row always fills the
// workspace edge to edge — no dead gap ever sits beside the palette.
//
//   1. the odontogram grows until every tooth hits its 64px ceiling,
//   2. then the rail grows past its guaranteed share, up to 640px,
//   3. then anything still left goes back to the chart column.
//
// The rail's floor stays its old proportional share, so anything at or below
// ~1600px renders exactly as it does today.
export const PROCEDURE_RAIL_BASE_SHARE =
  "max(var(--procedure-rail-min-width), min(33%, var(--procedure-rail-base-max-width)))";

export function getProcedureRailTrackStyle() {
  // Width the row has spare once the chart has taken all it can use. Negative
  // on narrow screens, where `clamp` falls back to the guaranteed share.
  const surplus =
    "calc(100% - var(--chart-content-max-width) - var(--chart-body-grid-gap))";
  return {
    gridTemplateColumns: [
      "minmax(0, 1fr)",
      `minmax(${PROCEDURE_RAIL_BASE_SHARE}, clamp(${PROCEDURE_RAIL_BASE_SHARE}, ${surplus}, var(--procedure-rail-max-width)))`,
    ].join(" "),
  };
}

export function resolveProtectedChartViewMode({ isDesktop, chartCanvasWidth, side }) {
  if (!isDesktop) return side;
  if (!chartCanvasWidth) return side;
  return canRenderFullArchAtWidth(chartCanvasWidth) ? "full" : side;
}

export function getChartCanvasContentWidth(node) {
  if (!node) return null;
  const rectWidth = node.getBoundingClientRect().width;
  const styles = typeof window !== "undefined" && typeof window.getComputedStyle === "function"
    ? window.getComputedStyle(node)
    : null;
  const paddingLeft = Number.parseFloat(styles?.paddingLeft || "0") || 0;
  const paddingRight = Number.parseFloat(styles?.paddingRight || "0") || 0;
  return Math.max(0, Math.round(rectWidth - paddingLeft - paddingRight));
}

export function buildMobileProcedurePalette(paletteOverride, onClose) {
  if (!isValidElement(paletteOverride)) return paletteOverride;
  const originalProps = paletteOverride.props || {};
  const callAndClose = (handler) => (...args) => {
    handler?.(...args);
    onClose?.();
  };

  return cloneElement(paletteOverride, {
    mobileSheet: true,
    onArm: originalProps.onArm
      ? (procedureId, ...args) => {
          originalProps.onArm(procedureId, ...args);
          if (procedureId) onClose?.();
        }
      : undefined,
    onPickWholeMouth: originalProps.onPickWholeMouth
      ? callAndClose(originalProps.onPickWholeMouth)
      : undefined,
    onPickEraser: originalProps.onPickEraser
      ? callAndClose(originalProps.onPickEraser)
      : undefined,
    onPickMultiTooth: originalProps.onPickMultiTooth
      ? callAndClose(originalProps.onPickMultiTooth)
      : undefined,
    onDefineMultiTooth: originalProps.onDefineMultiTooth
      ? callAndClose(originalProps.onDefineMultiTooth)
      : undefined,
  });
}

// =====================================================================
// ChartingTab — Phase 6 R1.2 (interactive)
// =====================================================================
// Surfaces the per-patient charting workflow:
//   * Loads the current finalized snapshot, the open draft (if any),
//     and the findings catalog.
//   * If no draft → shows the snapshot read-only with a "Start
//     charting" CTA. If a snapshot exists, the CTA opens a small
//     "Transfer previous?" / "Start fresh" prompt.
//   * If a draft is open → renders the interactive chart with the
//     findings palette, mode toggle, eraser, Done button, and discard.
//   * Done charting is gated to admin/doctor only (chart.finalize).
//   * Snapshot history list lives at the bottom; clicking opens a
//     read-only view of any past snapshot.
// =====================================================================

const FINALIZE_DEBOUNCE_MS = 500;
const LOADED_PEN_STORAGE_KEY = "dental-os.charting.loadedPen";

export function customFindingSaveErrorMessage(error) {
  const raw = String(error?.message || "").toLowerCase();
  const details = String(error?.details || "").toLowerCase();
  const code = String(error?.code || "");
  const combined = `${raw} ${details}`;

  if (code === "23505" || combined.includes("uq_chart_finding_codes_clinic")) {
    return "That custom finding already exists for this clinic.";
  }
  if (combined.includes("reserved code")) {
    return "That code is reserved for a built-in finding. Use a different name.";
  }
  if (code === "23514" || combined.includes("chart_finding_codes_code_format")) {
    return "Use a shorter custom finding name. The internal code must be 40 characters or fewer.";
  }
  if (code === "42501" || combined.includes("permission denied")) {
    return "You do not have permission to manage custom findings.";
  }
  return "Could not save custom finding.";
}

function loadStoredPen() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LOADED_PEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.kind === "finding" && typeof parsed.code === "string") {
      return { kind: "finding", code: parsed.code };
    }
  } catch {
    return null;
  }
  return null;
}

function formatDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
  } catch { return d; }
}

function formatCompletedWorkTarget(item) {
  const teeth = Array.isArray(item?.tooth_numbers) ? item.tooth_numbers.filter(Boolean) : [];
  const surfaces = Array.isArray(item?.surface_codes) ? item.surface_codes.filter(Boolean) : [];
  const toothText = teeth.length === 0 ? null : `${teeth.length > 1 ? "Teeth" : "Tooth"} ${teeth.join(", ")}`;
  const surfaceText = surfaces.length === 0 ? null : surfaces.join("");
  return [toothText, surfaceText].filter(Boolean).join(" · ");
}

function normalizeHistoryEntry(entry) {
  if (!entry) return entry;
  const snapshotId = entry.snapshot_id ?? null;
  return {
    ...entry,
    id: entry.entry_id ?? snapshotId,
    snapshot_id: snapshotId,
    entry_type: entry.entry_type ?? "current_chart",
    layer_name: entry.layer_name ?? "Current Chart",
    occurred_at: entry.occurred_at ?? entry.finalized_at,
    doctor_name: entry.doctor_name ?? entry.finalized_by_name,
    procedures: Array.isArray(entry.procedures) ? entry.procedures : [],
  };
}

function sortHistoryEntries(entries) {
  return [...(entries ?? [])].sort((a, b) => {
    const aTime = a?.occurred_at ? new Date(a.occurred_at).getTime() : 0;
    const bTime = b?.occurred_at ? new Date(b.occurred_at).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
  });
}

function normalizeCompletedWorkItems(items, fallback = {}) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    visit_id: item.visit_id ?? fallback.visit_id ?? null,
    completed_at: item.completed_at ?? fallback.completed_at ?? null,
    performed_by_name: item.performed_by_name ?? fallback.performed_by_name ?? null,
  }));
}

function ChartHistoryStrip({
  history,
  snapshot,
  showingViewed,
  viewingSnapshot,
  onClickHistoryEntry,
  open,
  onToggle,
}) {
  const visibleHistory = history;
  const currentEntry = history.find((entry) => entry.is_current) ?? history[history.length - 1] ?? null;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-slate-50/70"
      data-testid="charting-history"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid="charting-history-toggle"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="min-w-0">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Chart history
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-600">
            {currentEntry ? `${currentEntry.layer_name} · ${formatDateTime(currentEntry.occurred_at)}` : "No entries"}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open ? (
        <div className="border-t border-slate-200 px-3 py-2">
          <ul className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-5" data-testid="charting-history-condensed">
            {visibleHistory.map((h) => {
              const isCurrent = !showingViewed && snapshot?.id === h.snapshot_id;
              const isViewing = viewingSnapshot?.id === h.snapshot_id;
              const procedures = normalizeCompletedWorkItems(h.procedures, {
                visit_id: h.visit_id,
                completed_at: h.occurred_at,
                performed_by_name: h.doctor_name,
              });
              return (
                <li key={`${h.entry_type}-${h.id}`} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onClickHistoryEntry(h)}
                    data-testid={`charting-history-${h.id}`}
                    className={`w-full min-w-0 rounded-lg border px-2.5 py-2 text-left text-xs transition ${
                      isViewing
                        ? "border-slate-900 bg-slate-900 text-white"
                        : isCurrent
                        ? "border-teal-300 bg-teal-50 text-teal-800"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    <span className="block truncate font-semibold">{h.layer_name}</span>
                    <span className="mt-0.5 block truncate text-[11px] opacity-75">
                      {formatDateTime(h.occurred_at)}
                    </span>
                    <span className="mt-0.5 block truncate text-[10px] opacity-70">
                      {formatDoctorName(h.doctor_name, "No doctor")}
                      {isCurrent ? " · current" : ""}
                    </span>
                  </button>
                  {procedures.length > 0 ? (
                    <CompletedWorkHistoryDetail entry={{ ...h, procedures }} />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function CompletedWorkHistoryDetail({ entry }) {
  return (
    <div
      className="mt-1.5 rounded-lg border border-emerald-100 bg-white p-2"
      data-testid={`completed-work-detail-${entry.id}`}
    >
      <ul className="space-y-1.5">
        {entry.procedures.map((item) => {
          const target = formatCompletedWorkTarget(item);
          return (
            <li key={item.id} className="text-xs">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-900">{item.procedure_name || "Procedure"}</p>
                  {target ? <p className="truncate text-slate-500">{target}</p> : null}
                </div>
                {item.performed_by_name ? (
                  <span className="shrink-0 text-slate-500">{item.performed_by_name}</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ChartingTab({
  patientId,
  patient,
  chairside = false,
  // Chairside-only: mode controlled by parent. When provided, the
  // 4-mode toggle (Chart / Tx Plan / Today / Future Need) renders
  // inside the first card, replacing the internal 2-mode toggle.
  // When not provided, ChartingTab uses its own internal chartMode
  // state (the original Charting/Tx Plan toggle behavior).
  chartMode: chartModeProp,
  onChartModeChange,
  disabledChairsideModes = [],
  // When set, replaces the FindingsPalette in the right-rail dock
  // with this React node. Doctor view passes a ProceduresPalette
  // when chartMode is non-Chart so painting moves from findings
  // to procedures without the chart layout shifting.
  paletteOverride = null,
  // Chairside-only aligned second row (Doctor Today mode). belowChartLeft
  // renders under the chart, belowChartRight under the palette, sharing
  // the chart's exact column split so the two cards line up and stretch
  // to equal height. Doctor puts Today's work on the left and the
  // Ongoing treatment card on the right.
  belowChartLeft = null,
  belowChartRight = null,
  // Chairside-only: a card that renders in the LEFT column directly under
  // the chart card, filling the dead space the taller palette rail leaves
  // there. Doctor Tx Plan mode puts the plan selector here so it sits
  // below the odontogram at the odontogram's width while the plan builder
  // still spans full width below.
  gapSlot = null,
  // When true, the palette rail matches the chart's height (row-driven)
  // and its list scrolls internally, instead of the palette sizing to its
  // own content. Doctor Today mode sets this so the palette doesn't stick
  // out taller than the tooth chart. Pair with ProceduresPalette fillHeight.
  // When both aligned second-row slots are populated, give Today's Work and
  // the active Ortho module equal desktop width instead of inheriting the
  // odontogram / procedure-palette ratio. Mobile remains one column.
  belowChartEqualColumns = false,
  matchPaletteHeight = false,
  // Chairside-only: when set + chartMode !== 'chart', tooth clicks
  // route here instead of into the findings draft. Doctor view
  // consumes the click to attach the armed procedure to the right
  // downstream surface (today / plan / future-need).
  onToothInterceptClick = null,
  // Chairside-only: erase tool armed state + change handler. Owned
  // by Doctor view so its keyboard shortcuts and other UI can read /
  // toggle it. When armed in intercept mode, Tooth's erase logic
  // fires and click-to-erase routes through onToothInterceptErase.
  interceptEraseArmed = false,
  onInterceptEraseChange = null,
  onToothInterceptErase = null,
  // Chairside-only: name + color of the currently armed procedure
  // so the header can show a sticky "Composite armed" chip and the
  // dentist's eye doesn't have to dart to the right rail before
  // every paint. Passed through from Doctor view's armed state.
  armedProcedureName = null,
  armedProcedureColor = null,
  // Chairside-only: clears the armed procedure when the user taps
  // the ✕ on the header chip. When set, the header chip is rendered
  // as a dismissable button; without it, the chip is read-only.
  // Doctor view sets this so the negative-space layout can hide the
  // full palette while a procedure is armed (chart goes full-width
  // inside its card), and ✕ brings the palette back to switch.
  onClearArmedProcedure = null,
  // Chairside-only: undo/redo wired to whatever stack the active
  // chairside mode owns (Today's draft, Tx Plan ops, Future Need
  // ops). Without these the toolbar Undo/Redo buttons stay tied
  // to the chart's findings draft and are dead in intercept mode.
  interceptOnUndo = null,
  interceptOnRedo = null,
  interceptCanUndo = false,
  interceptCanRedo = false,
  // Chairside intercept notes. Used only by Future Need mode so the
  // tooth note pill writes to treatment_needs.notes instead of the
  // chart draft or visit note.
  interceptNoteTextByTooth = null,
  onToothInterceptNoteSave = null,
  // Optional planDots-by-FDI overlay (e.g. amber for Today,
  // violet for Tx Plan). Doctor view derives this from the
  // current visit draft + active plan + future needs so painted
  // procedures show up on the chart as colored dots above the
  // tooth number — same indicator the OverviewChart uses for
  // accepted plan items.
  planDotsByTooth = null,
  // Dim existing findings (chart layer) by ~60% opacity when true.
  // Used in chairside non-chart modes so painted procedures stand out
  // and existing findings read as background context.
  dimFindings = false,
  // Overlay paint layer — synthetic chart state + findings catalog
  // for painted procedures. Renders on top of the base findings so
  // the dentist sees their painted Today / Tx Plan / Future Need
  // procedures as colored surfaces, not just dots.
  overlayState = null,
  overlayFindings = null,
  stackSummaryByTooth = null,
  // Chairside-only: optional content rendered INSIDE the chart card's
  // left column, below the tooth chart canvas. Used by Doctor view to
  // slot the active mode's list (Today's work / Tx Plan builder /
  // Future Needs) into the empty space the chart row already has —
  // so painting a tooth lands the new line just below the chart
  // without narrowing the chart horizontally. Palette stays on the
  // right rail; mode list lives below the chart in the same column.
  belowChartCanvas = null,
  // When true, belowChartCanvas renders full-width BELOW the chart+palette
  // grid (spanning both columns) instead of nested in the left column.
  // Used by the Tx Plan mode so the appointment board gets the full width.
  belowChartCanvasFullWidth = false,
  // Chairside-only: optional content rendered INSIDE the tooth-chart
  // card itself, just under the ToothChart component. Used for the
  // whole-mouth procedure ribbon in Today mode — chips for Oral
  // Prophylaxis / Pano X-ray / etc. that have no tooth to attach
  // to but still belong on the chart visually so the doctor sees
  // every booked-for-today item from a single glance at the chart.
  chartFooter = null,
  // Multi-tooth groupings (Bridge / Splint / RPD / Denture / Crown).
  // ToothChart frames the contiguous teeth with the kind's color +
  // label and stamps A/P chips for abutment/pontic roles. Doctor view
  // computes this from the active Tx Plan's items in tx_plan mode so
  // chairside reads the same as the patient-tab Tx Plan chart.
  multiToothGroups = null,
  // Optional content rendered inside the chart-card toolbar row,
  // taking the empty space to the right of the toggle/undo buttons.
  // Used by Doctor view's Multi-Tooth Tool to show its "Tap adjacent
  // teeth..." banner inside the chart card instead of a separate row
  // above it.
  chartToolbarSlot = null,
  // Chairside-only: lets Doctor view read the same base chart layer
  // ToothChart is rendering, so bridge / multi-tooth tools can react
  // to missing teeth already marked in Initial Charting.
  onBaseChartStateChange = null,
  // Tutorial/demo mode injects local chart state and palette data so
  // the real charting UI can be taught without reading or writing Supabase.
  tutorialDemo = null,
}) {
  // chairside=true is set by Doctor view. It hides the internal
  // Charting/Tx Plan mode toggle (Doctor view's section 4 owns Tx
  // Plan now) and the snapshot history strip (chairside should
  // optimize for the active visit, not the audit trail).
  const { clinicRole } = useAuth();
  const canWrite     = ["clinic_admin","doctor","assistant"].includes(clinicRole);
  const canFinalize  = ["clinic_admin","doctor"].includes(clinicRole);
  const canManageFindings = ["clinic_admin","doctor"].includes(clinicRole);
  const tutorialDemoMode = !!tutorialDemo;

  // V1-style top-level mode toggle: 'charting' (paint findings) or
  // 'tx-plan' (paint procedures + builder, mounts the TreatmentTab
  // component inline). Cameron locked this so the dentist can flip
  // straight from charting findings into building a Tx Plan without
  // jumping between top-level tabs.
  const [chartMode, setChartMode] = useState("charting");

  const [findings, setFindings]   = useState([]);
  const [paletteCategories, setPaletteCategories] = useState([]);
  const [palettePreferences, setPalettePreferences] = useState(null);
  const [snapshot, setSnapshot]   = useState(null);
  const [draft, setDraft]         = useState(null);          // { id, state, base_snapshot_id }
  const [history, setHistory]     = useState([]);            // [{ snapshot_id, finalized_at, ... }]
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [viewingSnapshot, setViewingSnapshot] = useState(null); // full snapshot when user clicks a date
  const [historyOpen, setHistoryOpen] = useState(false);

  const [mode, setMode] = useState(() => defaultDentitionFromDOB(patient?.date_of_birth));

  // Tablet landscape keeps the chart one-column so full-mouth can fit.
  // Wide desktop docks the palette rail; below 900px the chart uses
  // half-arch views so tap targets stay usable.
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const isPaletteRailDocked = useMediaQuery(`(min-width: ${PALETTE_RAIL_BREAKPOINT_PX}px)`);
  // Protected side toggle: 'right' | 'left'. Used below mobile width
  // and whenever the measured chart canvas gets too tight for full arch.
  const [mobileSide, setMobileSide] = useState("right");
  const [chartCardNode, setChartCardNode] = useState(null);
  const [chartCanvasWidth, setChartCanvasWidth] = useState(() => (
    tutorialDemoMode ? SIDE_VIEW_BREAKPOINT : null
  ));
  const effectiveViewMode = resolveProtectedChartViewMode({
    isDesktop,
    chartCanvasWidth,
    side: mobileSide,
  });
  const usingProtectedSideView = effectiveViewMode !== "full";

  const [active, setActive]            = useState(() => tutorialDemo?.initialActive ?? loadStoredPen());     // { kind: 'finding'|'erase', code? } | null
  const [noteOpenFor, setNoteOpenFor]  = useState(null);     // FDI
  const [readOnlyNoteFor, setReadOnlyNoteFor] = useState(null); // FDI for snapshot-view note
  const [interceptNoteOpenFor, setInterceptNoteOpenFor] = useState(null); // FDI for layer-specific chairside notes

  const [addCustomOpen, setAddCustomOpen]       = useState(false);
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);
  const [pendingTutorialMissing, setPendingTutorialMissing] = useState(null);
  const [busy, setBusy]                   = useState({ start: false, save: false, finalize: false, discard: false, finding: false });
  const [loading, setLoading]             = useState(() => !tutorialDemoMode);
  const [error, setError]                 = useState(null);
  const [showStartChoice, setShowStartChoice] = useState(false);
  const chartFindings = useMemo(() => {
    const byCode = new Map();
    for (const f of findings) byCode.set(f.code, normalizeChartFinding(f));
    for (const f of flattenPaletteFindings(paletteCategories)) {
      byCode.set(f.code, normalizeChartFinding({ ...byCode.get(f.code), ...f }));
    }
    return Array.from(byCode.values()).sort((a, b) => Number(a.display_order ?? 0) - Number(b.display_order ?? 0));
  }, [findings, paletteCategories]);
  const selectedFinding = useMemo(() => {
    if (active?.kind !== "finding" || !active.code) return null;
    return chartFindings.find((f) => f.code === active.code) ?? null;
  }, [active, chartFindings]);
  const paletteCodeGroups = useMemo(() => buildPaletteCodeGroups(paletteCategories), [paletteCategories]);
  const activeCompletedWork = useMemo(() => (
    draft
      ? (draft.include_completed_work ? normalizeCompletedWorkItems(snapshot?.completed_work ?? []) : [])
      : ((viewingSnapshot ?? snapshot)?.include_completed_work === false
        ? []
        : normalizeCompletedWorkItems(viewingSnapshot?.completed_work ?? snapshot?.completed_work ?? []))
  ), [draft, viewingSnapshot, snapshot]);
  const completedWorkByTooth = useMemo(() => buildCompletedWorkByTooth(activeCompletedWork), [activeCompletedWork]);

  useEffect(() => {
    if (tutorialDemoMode) return;
    if (typeof window === "undefined") return;
    try {
      if (active?.kind === "finding" && active.code) {
        window.localStorage.setItem(LOADED_PEN_STORAGE_KEY, JSON.stringify({ kind: "finding", code: active.code }));
      } else {
        window.localStorage.removeItem(LOADED_PEN_STORAGE_KEY);
      }
    } catch {
      // Local storage is a convenience for pen persistence, not a save path.
    }
  }, [active, tutorialDemoMode]);

  useEffect(() => {
    if (!tutorialDemoMode) return;
    setFindings(Array.isArray(tutorialDemo.findings) ? tutorialDemo.findings : []);
    setPaletteCategories(Array.isArray(tutorialDemo.paletteCategories) ? tutorialDemo.paletteCategories : []);
    setPalettePreferences(tutorialDemo.palettePreferences ?? null);
    setSnapshot(tutorialDemo.snapshot ?? null);
    setDraft({
      id: tutorialDemo.draftId || "tutorial-chart-draft",
      state: tutorialDemo.state ?? EMPTY_STATE,
      base_snapshot_id: tutorialDemo.baseSnapshotId ?? null,
      include_completed_work: !!tutorialDemo.includeCompletedWork,
    });
    setHistory(sortHistoryEntries((tutorialDemo.history ?? []).map(normalizeHistoryEntry)));
    setHistoryLoaded(true);
    setViewingSnapshot(null);
    setHistoryOpen(false);
    setActive(tutorialDemo.initialActive ?? null);
    setLoading(false);
    setError(null);
  }, [tutorialDemoMode, tutorialDemo]);

  useEffect(() => {
    if (!chairside) return;
    if (!chartModeProp || chartModeProp === "chart") return;
    setActive(null);
    setMobilePaletteOpen(false);
    setNoteOpenFor(null);
    setInterceptNoteOpenFor(null);
  }, [chairside, chartModeProp]);

  useEffect(() => {
    if (chartMode === "charting") return;
    setActive(null);
    setMobilePaletteOpen(false);
    setNoteOpenFor(null);
  }, [chartMode]);

  // ----- debounced autosave -----
  const saveTimerRef = useRef(null);
  const queuedStateRef = useRef(null);
  const queueSave = useCallback((draftId, nextState) => {
    if (tutorialDemoMode) {
      queuedStateRef.current = null;
      return;
    }
    queuedStateRef.current = nextState;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const payload = queuedStateRef.current;
      saveTimerRef.current = null;
      setBusy((b) => ({ ...b, save: true }));
      const { error: e } = await supabase.rpc("save_chart_draft", {
        p_draft_id: draftId, p_state: payload,
      });
      setBusy((b) => ({ ...b, save: false }));
      if (e) toast.error(e.message ?? "Could not autosave the chart.");
    }, FINALIZE_DEBOUNCE_MS);
  }, [tutorialDemoMode]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  // ----- initial load -----
  const reload = useCallback(async () => {
    if (tutorialDemoMode) return;
    setLoading(true); setError(null);
    const [cat, palette, snap, drf, hist] = await Promise.all([
      supabase.rpc("list_chart_findings"),
      supabase.rpc("list_chart_palette"),
      supabase.rpc("get_chart_current",  { p_patient_id: patientId }),
      supabase.rpc("get_chart_draft",    { p_patient_id: patientId }),
      supabase.rpc("list_chart_history", { p_patient_id: patientId }),
    ]);
    setLoading(false);
    if (cat.error)  { setError(cat.error.message  ?? "Could not load findings."); return; }
    if (palette.error) { setError(palette.error.message ?? "Could not load charting palette."); return; }
    if (snap.error) { setError(snap.error.message ?? "Could not load chart.");    return; }
    if (drf.error)  { setError(drf.error.message  ?? "Could not load draft.");    return; }
    if (hist.error) { setError(hist.error.message ?? "Could not load history."); return; }
    setFindings(cat.data ?? []);
    setPaletteCategories(palette.data?.categories ?? []);
    setPalettePreferences(palette.data?.preferences ?? null);
    setSnapshot(snap.data ? { id: snap.data.snapshot_id, ...snap.data } : null);
    // Normalise the get_chart_draft response so the rest of the
    // component can use `draft.id`. The RPC returns `draft_id` (matches
    // the chart_drafts.id column) but the rest of this code expects the
    // shape returned by start_chart_draft, which we've been mapping to
    // `id` in startCharting. Without this normalisation,
    // `draft.id` is undefined after a page reload and the finalize call
    // fires without p_draft_id, which PostgREST rejects with
    //   Could not find the function public.finalize_chart(p_reason)
    setDraft(drf.data ? {
      id: drf.data.draft_id,
      state: drf.data.state ?? EMPTY_STATE,
      base_snapshot_id: drf.data.base_snapshot_id ?? null,
      include_completed_work: !!drf.data.include_completed_work,
    } : null);
    setHistory(sortHistoryEntries((hist.data ?? []).map(normalizeHistoryEntry)));
    setHistoryLoaded(true);
  }, [patientId, tutorialDemoMode]);

  useEffect(() => { reload(); }, [reload]);

  // Re-set default mode when patient DOB changes (e.g. fresh patient load).
  useEffect(() => {
    setMode(defaultDentitionFromDOB(patient?.date_of_birth));
  }, [patient?.date_of_birth]);

  // (Mobile side toggle is purely user-driven; defaults to 'right'.)

  useEffect(() => {
    if (tutorialDemoMode) return undefined;
    const node = chartCardNode;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const updateWidth = () => setChartCanvasWidth(getChartCanvasContentWidth(node));
    updateWidth();
    const observer = new ResizeObserver(() => {
      setChartCanvasWidth(getChartCanvasContentWidth(node));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [chartCardNode, tutorialDemoMode]);

  // If the window is dragged ABOVE the docked palette breakpoint while the
  // mobile palette sheet is open, dismiss it — otherwise the user ends
  // up with both a sidebar AND a bottom sheet visible.
  useEffect(() => {
    if (isPaletteRailDocked && mobilePaletteOpen) setMobilePaletteOpen(false);
  }, [isPaletteRailDocked, mobilePaletteOpen]);

  // ----- start charting -----
  const startCharting = useCallback(async (transferPrevious) => {
    if (tutorialDemoMode) {
      setDraft({
        id: tutorialDemo?.draftId || "tutorial-chart-draft",
        state: tutorialDemo?.state ?? EMPTY_STATE,
        base_snapshot_id: tutorialDemo?.baseSnapshotId ?? null,
        include_completed_work: !!tutorialDemo?.includeCompletedWork,
      });
      setShowStartChoice(false);
      return;
    }
    setBusy((b) => ({ ...b, start: true }));
    setShowStartChoice(false);
    const { data, error: e } = await supabase.rpc("start_chart_draft", {
      p_patient_id: patientId, p_transfer_previous: !!transferPrevious,
    });
    setBusy((b) => ({ ...b, start: false }));
    if (e) { toast.error(e.message ?? "Could not start charting."); return; }
    setDraft({
      id: data.draft_id,
      state: data.state ?? EMPTY_STATE,
      base_snapshot_id: data.base_snapshot_id,
      include_completed_work: !!data.include_completed_work,
    });
    toast.success(transferPrevious ? "Started from previous chart." : "Started a fresh chart.");
  }, [patientId, tutorialDemoMode, tutorialDemo]);

  const onClickStart = useCallback(() => {
    if (snapshot) setShowStartChoice(true);
    else startCharting(false);
  }, [snapshot, startCharting]);

  // ----- undo / redo -----
  // Local in-memory stacks of chart-state JSON snapshots. Each user
  // mutation pushes the PREVIOUS state to undoStack and clears redoStack.
  // Undo pops to previous + pushes current to redoStack. Capped at 50
  // to keep memory bounded; reset whenever the draft changes.
  const UNDO_LIMIT = 50;
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const undoCount = undoStack.length;
  const redoCount = redoStack.length;

  // Reset both stacks whenever the active draft changes (start / finalize
  // / discard / patient switch).
  useEffect(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, [draft?.id]);

  // ----- mutate helpers -----
  // `recordHistory` controls whether this mutation is added to the undo
  // stack. Undo / Redo themselves call with recordHistory=false.
  const updateDraftState = useCallback((nextState, recordHistory = true) => {
    if (!draft) return;
    if (recordHistory) {
      setUndoStack((s) => {
        const next = [...s, draft.state];
        return next.length > UNDO_LIMIT ? next.slice(next.length - UNDO_LIMIT) : next;
      });
      setRedoStack([]);          // standard undo/redo: new edit clears redo
    }
    setDraft((d) => (d ? { ...d, state: nextState } : d));
    queueSave(draft.id, nextState);
  }, [draft, queueSave]);

  const commitDraftState = useCallback(async (nextState, recordHistory = true) => {
    if (!draft) return false;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      queuedStateRef.current = null;
    }
    if (tutorialDemoMode) {
      if (recordHistory) {
        setUndoStack((s) => {
          const next = [...s, draft.state];
          return next.length > UNDO_LIMIT ? next.slice(next.length - UNDO_LIMIT) : next;
        });
        setRedoStack([]);
      }
      setDraft((d) => (d ? { ...d, state: nextState } : d));
      return true;
    }
    setBusy((b) => ({ ...b, save: true }));
    const { error: e } = await supabase.rpc("save_chart_draft", {
      p_draft_id: draft.id,
      p_state: nextState,
    });
    setBusy((b) => ({ ...b, save: false }));
    if (e) {
      toast.error(e.message ?? "Could not save the chart.");
      return false;
    }
    if (recordHistory) {
      setUndoStack((s) => {
        const next = [...s, draft.state];
        return next.length > UNDO_LIMIT ? next.slice(next.length - UNDO_LIMIT) : next;
      });
      setRedoStack([]);
    }
    setDraft((d) => (d ? { ...d, state: nextState } : d));
    return true;
  }, [draft, tutorialDemoMode]);

  const onUndo = useCallback(() => {
    if (!draft || undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setRedoStack((s) => [...s, draft.state]);
    setDraft((d) => (d ? { ...d, state: prev } : d));
    queueSave(draft.id, prev);
  }, [draft, undoStack, queueSave]);

  const onRedo = useCallback(() => {
    if (!draft || redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setRedoStack((s) => s.slice(0, -1));
    setUndoStack((s) => [...s, draft.state]);
    setDraft((d) => (d ? { ...d, state: next } : d));
    queueSave(draft.id, next);
  }, [draft, redoStack, queueSave]);

  const requestTutorialMissingConfirmation = useCallback((fdi, code) => {
    setPendingTutorialMissing({ fdi, code });
    window.dispatchEvent(new CustomEvent("tutorial:missing-confirmation-requested", {
      detail: { toothNumber: String(fdi), findingCode: code },
    }));
  }, []);

  useEffect(() => {
    if (!tutorialDemoMode) return undefined;
    function onConfirmMissing() {
      if (!pendingTutorialMissing || !draft) return;
      updateDraftState(applyToothFinding(
        draft.state,
        pendingTutorialMissing.fdi,
        pendingTutorialMissing.code,
        paletteCodeGroups[pendingTutorialMissing.code] || [],
      ));
      window.dispatchEvent(new CustomEvent("tutorial:missing-confirmed", {
        detail: {
          toothNumber: String(pendingTutorialMissing.fdi),
          findingCode: pendingTutorialMissing.code,
        },
      }));
      setPendingTutorialMissing(null);
    }
    function onCancelMissing() {
      setPendingTutorialMissing(null);
    }
    window.addEventListener("tutorial:missing-confirmation-confirmed", onConfirmMissing);
    window.addEventListener("tutorial:missing-confirmation-cancelled", onCancelMissing);
    return () => {
      window.removeEventListener("tutorial:missing-confirmation-confirmed", onConfirmMissing);
      window.removeEventListener("tutorial:missing-confirmation-cancelled", onCancelMissing);
    };
  }, [draft, paletteCodeGroups, pendingTutorialMissing, tutorialDemoMode, updateDraftState]);

  // Cmd+Z / Cmd+Shift+Z keyboard shortcuts (Ctrl+Z / Ctrl+Y on Win).
  useEffect(() => {
    if (!draft) return;
    function onKey(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      // Skip when an input/textarea has focus — let the field's native undo work.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey)            { e.preventDefault(); onUndo(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); onRedo(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft, onUndo, onRedo]);

  const onApplyFindingSurface = useCallback((fdi, surface) => {
    if (!draft || active?.kind !== "finding") return;
    const f = chartFindings.find((x) => x.code === active.code);
    if (!f) return;
    if (!f.requires_surfaces) {
      // Whole-tooth tool clicked on a surface → still applies whole-tooth.
      if (tutorialDemoMode && f.code === "missing") {
        requestTutorialMissingConfirmation(fdi, f.code);
        return;
      }
      updateDraftState(applyToothFinding(draft.state, fdi, f.code, paletteCodeGroups[f.code] || []));
    } else {
      updateDraftState(applySurfaceFindingWithAutoFlags(draft.state, fdi, surface, f.code));
    }
  }, [active, draft, chartFindings, paletteCodeGroups, requestTutorialMissingConfirmation, tutorialDemoMode, updateDraftState]);

  const onEraseSurfaceClick = useCallback((fdi, surface) => {
    if (!draft || active?.kind !== "erase") return;
    updateDraftState(eraseSurface(draft.state, fdi, surface));
  }, [active, draft, updateDraftState]);

  const onClearToothClick = useCallback((fdi) => {
    if (!draft || active?.kind !== "erase") return;
    updateDraftState(clearTooth(draft.state, fdi));
  }, [active, draft, updateDraftState]);

  const onEditSummaryFinding = useCallback((entry) => {
    if (!entry?.code || !draft) return;
    setActive({ kind: "finding", code: entry.code });
    if (!isDesktop) setMobilePaletteOpen(true);
  }, [draft, isDesktop]);

  const onRemoveSummaryFinding = useCallback((entry) => {
    if (!draft || !entry?.toothNumber || !entry?.code) return;
    commitDraftState(removeToothFinding(draft.state, entry.toothNumber, entry.code));
  }, [commitDraftState, draft]);

  // ----- note pill -----
  const onOpenNote = useCallback((fdi) => {
    if (draft) setNoteOpenFor(fdi);
    else if (snapshot || viewingSnapshot) setReadOnlyNoteFor(fdi);
  }, [draft, snapshot, viewingSnapshot]);

  const onSaveNote = useCallback((text) => {
    if (!draft) return;
    updateDraftState(setNote(draft.state, noteOpenFor, text));
    setNoteOpenFor(null);
  }, [draft, noteOpenFor, updateDraftState]);

  const onDeleteNote = useCallback(() => {
    if (!draft) return;
    updateDraftState(setNote(draft.state, noteOpenFor, ""));
    setNoteOpenFor(null);
  }, [draft, noteOpenFor, updateDraftState]);

  const onSaveInterceptNote = useCallback(async (text) => {
    if (interceptNoteOpenFor == null || !onToothInterceptNoteSave) return;
    const ok = await onToothInterceptNoteSave(String(interceptNoteOpenFor), text);
    if (ok !== false) setInterceptNoteOpenFor(null);
  }, [interceptNoteOpenFor, onToothInterceptNoteSave]);

  const onDeleteInterceptNote = useCallback(async () => {
    if (interceptNoteOpenFor == null || !onToothInterceptNoteSave) return;
    const ok = await onToothInterceptNoteSave(String(interceptNoteOpenFor), "");
    if (ok !== false) setInterceptNoteOpenFor(null);
  }, [interceptNoteOpenFor, onToothInterceptNoteSave]);

  // ----- finalize / discard -----
  const [finalizeReason, setFinalizeReason] = useState("");
  const [showFinalizePrompt, setShowFinalizePrompt] = useState(false);
  const [showDiscardPrompt, setShowDiscardPrompt]   = useState(false);

  const flushSaveBeforeAction = useCallback(async () => {
    if (tutorialDemoMode) return;
    if (!saveTimerRef.current || !draft) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    const payload = queuedStateRef.current ?? draft.state;
    setBusy((b) => ({ ...b, save: true }));
    const { error: e } = await supabase.rpc("save_chart_draft", {
      p_draft_id: draft.id, p_state: payload,
    });
    setBusy((b) => ({ ...b, save: false }));
    if (e) throw e;
  }, [draft, tutorialDemoMode]);

  const onFinalize = useCallback(async () => {
    if (!draft || !canFinalize) return;
    if (tutorialDemoMode) {
      setShowFinalizePrompt(false);
      setFinalizeReason("");
      toast.info("Tutorial demo only. No chart was finalized.");
      return;
    }
    try { await flushSaveBeforeAction(); }
    catch (e) { toast.error(e.message ?? "Could not save the chart before finalizing."); return; }
    setBusy((b) => ({ ...b, finalize: true }));
    const { data, error: e } = await supabase.rpc("finalize_chart", {
      p_draft_id: draft.id, p_reason: finalizeReason.trim() || null,
    });
    setBusy((b) => ({ ...b, finalize: false }));
    if (e) { toast.error(e.message ?? "Could not finalize the chart."); return; }
    toast.success("Charting finalized.");
    setShowFinalizePrompt(false);
    setFinalizeReason("");
    setDraft(null);
    setActive(null);
    await reload();
  }, [draft, canFinalize, finalizeReason, flushSaveBeforeAction, reload, tutorialDemoMode]);

  const onChartModeSelect = useCallback(async (nextMode) => {
    if (tutorialDemoMode) {
      setChartMode(nextMode);
      return;
    }
    if (nextMode === "tx-plan" && draft) {
      try { await flushSaveBeforeAction(); }
      catch (e) {
        toast.error(e.message ?? "Could not save the chart before opening Tx Plan.");
        return;
      }
      const { data, error: e } = await supabase.rpc("get_chart_draft", { p_patient_id: patientId });
      if (e) {
        toast.error(e.message ?? "Could not reload the saved chart draft.");
        return;
      }
      if (data) {
        setDraft({
          id: data.draft_id,
          state: data.state ?? EMPTY_STATE,
          base_snapshot_id: data.base_snapshot_id ?? null,
          include_completed_work: !!data.include_completed_work,
        });
      }
    }
    setChartMode(nextMode);
  }, [draft, flushSaveBeforeAction, patientId, tutorialDemoMode]);

  const onDiscard = useCallback(async () => {
    if (!draft) return;
    if (tutorialDemoMode) {
      setShowDiscardPrompt(false);
      setDraft((d) => (d ? { ...d, state: tutorialDemo?.state ?? EMPTY_STATE } : d));
      setActive(tutorialDemo?.initialActive ?? null);
      setUndoStack([]);
      setRedoStack([]);
      toast.info("Tutorial demo reset. Nothing was discarded from the patient record.");
      return;
    }
    setBusy((b) => ({ ...b, discard: true }));
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    const { error: e } = await supabase.rpc("discard_chart_draft", { p_draft_id: draft.id });
    setBusy((b) => ({ ...b, discard: false }));
    if (e) { toast.error(e.message ?? "Could not discard the draft."); return; }
    toast.success("Draft discarded.");
    setShowDiscardPrompt(false);
    setDraft(null);
    setActive(null);
    await reload();
  }, [draft, reload, tutorialDemoMode, tutorialDemo]);

  // ----- custom finding -----
  const onSaveCustomFinding = useCallback(async (payload) => {
    if (tutorialDemoMode) {
      setAddCustomOpen(false);
      toast.info("Tutorial demo only. Custom pens are not saved.");
      return;
    }
    setBusy((b) => ({ ...b, finding: true }));
    const { error: e } = await supabase.rpc("upsert_chart_finding", { p_payload: payload });
    setBusy((b) => ({ ...b, finding: false }));
    if (e) { toast.error(customFindingSaveErrorMessage(e)); return; }
    toast.success("Custom finding added.");
    setAddCustomOpen(false);
    // Reload both catalogs so the visible backend-owned palette updates immediately.
    const [cat, palette] = await Promise.all([
      supabase.rpc("list_chart_findings"),
      supabase.rpc("list_chart_palette"),
    ]);
    if (!cat.error) setFindings(cat.data ?? []);
    if (!palette.error) {
      setPaletteCategories(palette.data?.categories ?? []);
      setPalettePreferences(palette.data?.preferences ?? null);
    }
  }, [tutorialDemoMode]);

  const onSavePalettePreferences = useCallback(async (nextPrefs) => {
    setPalettePreferences((current) => ({ ...(current || {}), ...(nextPrefs || {}) }));
    if (tutorialDemoMode) return;
    const { data, error: e } = await supabase.rpc("save_chart_palette_preferences", {
      p_payload: nextPrefs || {},
    });
    if (e) {
      toast.error(e.message ?? "Could not save chart palette preferences.");
      return;
    }
    if (data) setPalettePreferences(data);
  }, [tutorialDemoMode]);

  // ----- snapshot history viewer -----
  const onClickSnapshot = useCallback(async (snapshotId) => {
    if (tutorialDemoMode) return;
    if (snapshotId === snapshot?.id) { setViewingSnapshot(null); return; }
    if (snapshotId === viewingSnapshot?.id) { setViewingSnapshot(null); return; }
    const { data, error: e } = await supabase.rpc("get_chart_snapshot", { p_id: snapshotId });
    if (e) { toast.error(e.message ?? "Could not load snapshot."); return; }
    setViewingSnapshot(data ? { id: snapshotId, ...data } : null);
  }, [snapshot?.id, viewingSnapshot, tutorialDemoMode]);

  const onClickHistoryEntry = useCallback(async (entry) => {
    if (entry.is_current || entry.snapshot_id === snapshot?.id) {
      setViewingSnapshot(null);
      return;
    }
    if (entry.snapshot_id) {
      await onClickSnapshot(entry.snapshot_id);
    }
  }, [snapshot?.id, onClickSnapshot]);

  // =====================================================================
  // Render
  const currentBaseChartState = useMemo(
    () => viewingSnapshot?.state ?? draft?.state ?? snapshot?.state ?? EMPTY_STATE,
    [viewingSnapshot, draft, snapshot],
  );
  const displayChartState = useMemo(
    () => applyCompletedWorkChartEffects(currentBaseChartState, activeCompletedWork),
    [currentBaseChartState, activeCompletedWork],
  );

  useEffect(() => {
    onBaseChartStateChange?.(displayChartState);
  }, [onBaseChartStateChange, displayChartState]);

  // =====================================================================
  if (loading) {
    return <div data-testid="charting-tab-loading" className="text-sm text-slate-400 px-2">Loading chart…</div>;
  }
  if (error) {
    return <div data-testid="charting-tab-error" className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700">{error}</div>;
  }

  // Determine what state to render.
  const showingDraft  = !!draft;
  // Chairside non-chart intercept mode — chart renders the snapshot
  // read-only-for-findings, but tooth clicks fire onToothInterceptClick
  // so the chart becomes a procedure-painting canvas without losing
  // the existing findings context.
  const inIntercept = chairside && chartModeProp && chartModeProp !== "chart" && !!onToothInterceptClick;
  const showingViewed = !!viewingSnapshot;
  const renderState   = displayChartState;
  const bridgeFindingGroups = buildBridgeFindingGroups(renderState, chartFindings);
  const chartMultiToothGroups = [
    ...(Array.isArray(multiToothGroups) ? multiToothGroups : []),
    ...bridgeFindingGroups,
  ];
  const isReadOnly    = !showingDraft;
  const noteText      = noteOpenFor != null ? getNoteText(draft?.state, noteOpenFor) : "";
  const readOnlyNoteText = readOnlyNoteFor != null
    ? getNoteText(viewingSnapshot?.state ?? snapshot?.state, readOnlyNoteFor)
    : "";
  const hasProcedureRail = !!(paletteOverride || (!inIntercept && showingDraft));
  const hasDockedProcedureRail = isPaletteRailDocked && hasProcedureRail;
  const procedureModeGridStyle = hasDockedProcedureRail
    ? {
        ...getProcedureModeGridStyle(hasProcedureRail, { dockPaletteRail: true }),
        ...getProcedureRailTrackStyle(),
      }
    : undefined;
  const belowChartGridStyle = getBelowChartEqualColumnsStyle({
    hasDockedProcedureRail,
    equalColumns: belowChartEqualColumns,
    hasLeftContent: !!belowChartLeft,
    hasRightContent: !!belowChartRight,
  }) || procedureModeGridStyle;
  const mobileProcedurePalette = buildMobileProcedurePalette(
    paletteOverride,
    () => setMobilePaletteOpen(false),
  );

  // Tx Plan mode mounts the existing TreatmentTab inline so the
  // dentist gets the painted-procedures + builder workflow without
  // leaving the Charting tab. Pure render swap — state for the
  // charting half (findings, draft, etc.) stays mounted, so the
  // dentist can flip back without losing work.
  // Chairside mode forces the Charting layer — the Tx Plan side is
  // owned by Doctor view's section 4. On the patient page (chairside
  // false) the toggle still renders so dentists can flip between
  // findings and plan-painting on the patient record.
  if (!chairside && chartMode === "tx-plan") {
    return (
      <div className="space-y-4" data-testid="charting-tab">
        <ChartModeToggle mode={chartMode} onChange={onChartModeSelect} />
        <TreatmentTab
          patientId={patientId}
          patient={patient}
          chartStateOverride={draft?.state ?? null}
          chartFirst
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="charting-tab">

      {chairside ? null : <ChartModeToggle mode={chartMode} onChange={onChartModeSelect} />}

      {/* Header / actions strip */}
      {/* Header — title block on top, then a separate row of toggles + actions
          so nothing crowds itself on tablet/phone. Lays out as a single row
          on lg+ if there's space.
          In chairside mode the 4-mode chairside toggle renders at the very
          top of this card so the dentist sees it as soon as they look at
          the chart. */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 space-y-3">
        {chairside && chartModeProp && onChartModeChange ? (
          <ChairsideModeToggle
            mode={chartModeProp}
            onChange={onChartModeChange}
            disabledModes={disabledChairsideModes}
          />
        ) : null}
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
              <HistoryIcon className="w-4 h-4 text-teal-600" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              {inIntercept ? (
                <>
                  <p className="text-sm font-semibold text-slate-900">
                    {chartModeProp === "tx_plan"     ? "Tx Plan painting"
                     : chartModeProp === "today"     ? "Today's procedures"
                     : chartModeProp === "future_need" ? "Future Need painting"
                     : "Painting"}
                  </p>
                  {interceptEraseArmed ? (
                    <p className="text-xs mt-0.5 inline-flex items-center gap-1.5 text-rose-700">
                      <Eraser className="w-3 h-3" />
                      Erase armed — tap a painted surface to remove it.
                    </p>
                  ) : armedProcedureName ? (
                    <div className="mt-0.5 inline-flex items-center gap-2 max-w-full" data-testid="charting-armed-chip">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-800 max-w-full"
                        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
                      >
                        <span
                          className="w-2 h-2 rounded-full ring-1 ring-black/10 shrink-0"
                          style={{ backgroundColor: armedProcedureColor || "#94a3b8" }}
                          aria-hidden
                        />
                        <span className="font-medium truncate">{armedProcedureName}</span>
                        {onClearArmedProcedure ? (
                          <button
                            type="button"
                            onClick={onClearArmedProcedure}
                            data-testid="charting-armed-clear"
                            aria-label="Clear armed procedure"
                            className="ml-0.5 -mr-0.5 p-0.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 shrink-0"
                          >
                            <XIcon className="w-3 h-3" />
                          </button>
                        ) : null}
                      </span>
                      <span className="text-[11px] text-slate-400 truncate">
                        Tap a tooth to attach.
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Pick a procedure from the palette, then tap a tooth to attach it.
                    </p>
                  )}
                </>
              ) : showingDraft ? (
                <>
                  <p className="text-sm font-semibold text-slate-900">Charting in progress</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Autosaves automatically.{busy.save ? " Saving…" : ""}
                  </p>
                </>
              ) : showingViewed ? (
                <>
                  <p className="text-sm font-semibold text-slate-900">Viewing past chart</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    Finalized {formatDateTime(viewingSnapshot.finalized_at)}
                    {viewingSnapshot.finalized_by_name ? <> by {viewingSnapshot.finalized_by_name}</> : null}
                  </p>
                </>
              ) : snapshot ? (
                <>
                  <p className="text-sm font-semibold text-slate-900">Current chart</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    Finalized {formatDateTime(snapshot.finalized_at)}
                    {snapshot.finalized_by_name ? <> by {snapshot.finalized_by_name}</> : null}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-slate-900">No chart yet</p>
                  <p className="text-xs text-slate-500 mt-0.5">This patient has never been charted.</p>
                </>
              )}
            </div>
          </div>

          {/* Right-hand actions only — toggles wrap to their own row below.
              In intercept mode (chairside non-chart) the dentist isn't
              editing findings — hide Edit chart / Discard / Done so the
              chart reads as a procedure-paint canvas. */}
          <div className="flex items-center gap-2 shrink-0">
            {inIntercept ? null : showingViewed ? (
              <button
                type="button"
                onClick={() => setViewingSnapshot(null)}
                data-testid="charting-back-to-current"
                className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1"
              >
                Back to current
              </button>
            ) : null}

            {/* Chart-edit chrome — Discard / Done charting / Edit chart —
                lives only in true Chart-editing flow. In intercept mode
                (chairside non-chart) the dentist isn't editing findings,
                so these CTAs would mislead. */}
            {inIntercept ? null : showingDraft ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowDiscardPrompt(true)}
                  disabled={busy.discard}
                  data-testid="charting-discard"
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-rose-400 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-1"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={tutorialDemoMode ? onFinalize : () => setShowFinalizePrompt(true)}
                  disabled={!canFinalize || busy.finalize}
                  title={canFinalize ? "Finalize this chart (Done charting)" : "Only doctors and clinic admins can finalize."}
                  data-testid="charting-done"
                  data-tutorial-id="charting-done-action"
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
                >
                  <Check className="w-3.5 h-3.5" /> Done charting
                </button>
              </>
            ) : !showingViewed ? (
              <button
                type="button"
                onClick={onClickStart}
                disabled={!canWrite || busy.start}
                data-testid="charting-start"
                title={snapshot ? "Open the latest chart in a new draft to make changes" : "Begin a new chart"}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
              >
                {busy.start ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smile className="w-3.5 h-3.5" />}
                {snapshot ? "Edit chart" : "Start charting"}
              </button>
            ) : null}
          </div>
        </div>

        {!chairside && !showingDraft && historyLoaded && history.length > 0 ? (
          <ChartHistoryStrip
            history={history}
            snapshot={snapshot}
            showingViewed={showingViewed}
            viewingSnapshot={viewingSnapshot}
            onClickHistoryEntry={onClickHistoryEntry}
            open={historyOpen}
            onToggle={() => setHistoryOpen((value) => !value)}
          />
        ) : null}

        {/* Toggle row — Permanent/Both/Primary always; mobile-only side toggle. */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-100">
          <DentitionToggle mode={mode} onChange={setMode} />
          {/* Side toggle is visible on small screens and whenever the
              measured chart canvas falls below the protected full-arch
              width. State stays mounted across resize. */}
          <div className={usingProtectedSideView ? "" : "min-[900px]:hidden"}>
            <SideToggle side={mobileSide} onChange={setMobileSide} />
          </div>
          {inIntercept && onInterceptEraseChange ? (
            <EraseToggle
              on={interceptEraseArmed}
              onChange={(v) => onInterceptEraseChange(v)}
            />
          ) : null}
          {inIntercept ? (
            // Intercept mode: route the toolbar buttons to whatever
            // chairside stack the active mode owns. Today → visit
            // draft (local), Tx Plan / Future Need → DB-persisted
            // ops stack (delete_plan_item / delete_open_treatment_need).
            <UndoRedoButtons
              undoCount={interceptCanUndo ? 1 : 0}
              redoCount={interceptCanRedo ? 1 : 0}
              onUndo={interceptOnUndo}
              onRedo={interceptOnRedo}
            />
          ) : showingDraft ? (
            <UndoRedoButtons
              undoCount={undoCount}
              redoCount={redoCount}
              onUndo={onUndo}
              onRedo={onRedo}
            />
          ) : null}
          {chartToolbarSlot ? (
            <div className="flex-1 min-w-0">
              {chartToolbarSlot}
            </div>
          ) : null}
        </div>
      </div>

      {/* Body: chart + palette sidebar.
          The rail layout switches at 900px (tablet-landscape and up).
          Palette and floating
          chip are rendered the same way regardless and toggled with
          Tailwind visibility utilities so resize across the breakpoint
          is smooth (no React mount/unmount flicker).
          When `belowChartCanvas` is supplied (chairside non-chart modes
          in Doctor view) it renders as a sibling card BELOW the chart
          inside the same left column — fills the empty space the
          palette already creates by stretching the row, instead of
          forcing the chart narrower with an outer side-by-side grid. */}
      <div
        className={getChartBodyGridClassName(hasDockedProcedureRail)}
        style={procedureModeGridStyle}
      >
        <div className="flex flex-col gap-3 min-w-0">
          <div
            ref={setChartCardNode}
            className={CHART_CARD_CLASSNAME}
            data-testid="charting-tooth-chart"
            data-tutorial-id="charting-tooth-chart"
          >
            {/* Empty state suppressed in intercept mode — even a never-
                charted patient should see an empty chart that's paintable
                for procedures. */}
            {(!showingDraft && !showingViewed && !snapshot && !inIntercept) ? (
              <EmptyState />
            ) : (
              <>
                <ToothChart
                  state={renderState}
                  findings={chartFindings}
                  mode={mode}
                  viewMode={effectiveViewMode}
                  planDotsByTooth={planDotsByTooth}
                  completedWorkByTooth={completedWorkByTooth}
                  dimFindings={dimFindings}
                  overlayState={overlayState}
                  overlayFindings={overlayFindings}
                  stackSummaryByTooth={stackSummaryByTooth}
                  multiToothGroups={chartMultiToothGroups.length > 0 ? chartMultiToothGroups : null}
                  /* In intercept mode we feed Tooth a synthetic
                     "finding-armed" active object so its surface
                     click handler fires; the click then routes
                     through our overridden onApplyFindingSurface
                     into onToothInterceptClick. The synthetic code
                     never matches a real finding so chart state
                     doesn't leak. */
                  active={inIntercept
                    ? (interceptEraseArmed
                        ? { kind: "erase" }
                        : { kind: "finding", code: "__doctor_intercept__" })
                    : (showingDraft ? active : null)}
                  readOnly={inIntercept ? false : isReadOnly}
                  onApplyFindingSurface={inIntercept
                    ? (fdi, surface) => onToothInterceptClick(fdi, surface)
                    : onApplyFindingSurface}
                  onEraseSurface={inIntercept
                    ? (fdi, surface) => onToothInterceptErase?.(fdi, surface)
                    : onEraseSurfaceClick}
                  onClearTooth={inIntercept
                    ? (fdi) => onToothInterceptErase?.(fdi, null)
                    : onClearToothClick}
                  canEditNote={inIntercept ? !!onToothInterceptNoteSave : undefined}
                  onOpenNote={inIntercept
                    ? (onToothInterceptNoteSave ? (fdi) => setInterceptNoteOpenFor(fdi) : () => {})
                    : onOpenNote}
                />
                {showingDraft && !inIntercept && selectedFinding ? (
                  <div
                    className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-950"
                    data-testid="charting-selected-pen-helper"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-700">
                      Selected pen
                    </span>
                    <span
                      className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10"
                      style={{ backgroundColor: selectedFinding.color_hex || "#0f766e" }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      Tap a tooth surface to apply{" "}
                      <strong>{selectedFinding.display_name || selectedFinding.name || selectedFinding.label || selectedFinding.code}</strong>.
                    </span>
                  </div>
                ) : null}
                {/* Base findings legend follows the initial chart layer
                    even while chairside modes paint procedures over it. */}
                <ChartFindingsLegend
                  state={renderState}
                  findings={chartFindings}
                  label={inIntercept ? "Charting Legend" : "Legend"}
                />
                {!inIntercept ? (
                  <>
                    <ChartedFindingsSummaryPanel
                      state={renderState}
                      findings={chartFindings}
                      readOnly={!showingDraft}
                      defaultCollapsed={!showingDraft}
                      completedWorkByTooth={completedWorkByTooth}
                      onEditFinding={onEditSummaryFinding}
                      onRemoveFinding={onRemoveSummaryFinding}
                    />
                  </>
                ) : null}
              </>
            )}
            {chartFooter ? (
              <div
                className="mt-3 pt-3 border-t border-slate-100"
                data-testid="charting-chart-footer"
              >
                {chartFooter}
              </div>
            ) : null}
          </div>
          {gapSlot ? (
            <div className="min-w-0" data-testid="charting-gap-slot">{gapSlot}</div>
          ) : null}
          {belowChartCanvas && !belowChartCanvasFullWidth ? (
            <div className="min-w-0" data-testid="charting-below-canvas">
              {belowChartCanvas}
            </div>
          ) : null}
        </div>

        {/* Procedures palette. From tablet-landscape width (900px) up it
            docks as the right rail; below that it collapses to the floating
            chip / bottom sheet on phones and portrait-held small tablets. */}
        {paletteOverride ? (
          matchPaletteHeight ? (
            // Cap the palette to the chart's height. The inner wrapper is
            // absolutely positioned (≥900px) so the palette contributes no
            // height to the grid row — the chart alone drives the row, and
            // the cell stretches the palette to match. ProceduresPalette
            // fillHeight makes its list the internal scroll area.
            <div className="hidden min-w-0 min-[900px]:relative min-[900px]:block min-[900px]:overflow-hidden" data-testid="charting-palette-rail-capped">
              <div className="min-[900px]:absolute min-[900px]:inset-0 min-[900px]:overflow-hidden">
                {paletteOverride}
              </div>
            </div>
          ) : (
            <div className={PALETTE_OVERRIDE_RAIL_CLASSNAME} data-testid="charting-procedure-palette-rail">
              {paletteOverride}
            </div>
          )
        ) : (!inIntercept && showingDraft) ? (
          <div className={PALETTE_RAIL_CLASSNAME}>
            <FindingsPalette
              findings={chartFindings}
              paletteCategories={paletteCategories}
              palettePreferences={palettePreferences}
              active={active}
              disabled={!canWrite}
              onPickFinding={(code) =>
                setActive((a) => (tutorialDemoMode || a?.kind !== "finding" || a.code !== code) ? { kind: "finding", code } : null)}
              onPickEraser={() =>
                setActive((a) => (a?.kind === "erase") ? null : { kind: "erase" })}
              onClear={() => setActive(null)}
              onOpenAddCustom={() => setAddCustomOpen(true)}
              onSavePreferences={onSavePalettePreferences}
              canManageFindings={canManageFindings}
              translateLoadedOnTabSwitch={!tutorialDemoMode}
            />
          </div>
        ) : null}
      </div>

      {/* Aligned second row (Doctor Today mode): a card under the chart
          (Today's work) and a card under the palette (Ongoing treatment),
          laid out on the SAME column template as the chart+palette grid
          above so the two cards line up edge-to-edge. Grid rows stretch
          both cells to equal height — with the h-full pass-through the two
          cards are always the same height no matter which has more content.
          Collapses to a single stacked column below the rail breakpoint,
          exactly like the chart grid. */}
      {(belowChartLeft || belowChartRight) ? (
        <div
          className={CHART_BELOW_GRID_CLASSNAME}
          style={belowChartGridStyle}
          data-testid="charting-below-grid"
        >
          <div className={CHART_BELOW_CELL_CLASSNAME} data-testid="charting-below-left">{belowChartLeft}</div>
          <div className={CHART_BELOW_CELL_CLASSNAME} data-testid="charting-below-right">{belowChartRight}</div>
        </div>
      ) : null}

      {/* Full-width belowChartCanvas (Tx Plan mode): spans the whole width
          below the chart+palette grid instead of being capped at the chart
          column, so the appointment board has room to breathe. */}
      {belowChartCanvas && belowChartCanvasFullWidth ? (
        <div className="min-w-0 mt-4" data-testid="charting-below-canvas">
          {belowChartCanvas}
        </div>
      ) : null}

      {/* Floating palette chip — visible until the palette rail docks.
          mounts in both modes; visibility is CSS-only so resize is
          smooth across the breakpoint. */}
      {showingDraft && !inIntercept ? (
        <FloatingPaletteChip
          active={active}
          findings={chartFindings}
          onClick={() => setMobilePaletteOpen(true)}
        />
      ) : null}

      {paletteOverride && inIntercept ? (
        <FloatingProcedurePaletteChip
          mode={chartModeProp}
          armedProcedureName={armedProcedureName}
          armedProcedureColor={armedProcedureColor}
          onClick={() => setMobilePaletteOpen(true)}
        />
      ) : null}

      {/* Modals + prompts */}
      {showStartChoice ? (
        <StartChoiceDialog
          onTransfer={() => startCharting(true)}
          onFresh={() => startCharting(false)}
          onClose={() => setShowStartChoice(false)}
        />
      ) : null}

      {showFinalizePrompt ? (
        <FinalizePrompt
          reason={finalizeReason}
          onChange={setFinalizeReason}
          busy={busy.finalize}
          onConfirm={onFinalize}
          onClose={() => setShowFinalizePrompt(false)}
        />
      ) : null}

      {showDiscardPrompt ? (
        <DiscardPrompt
          busy={busy.discard}
          onConfirm={onDiscard}
          onClose={() => setShowDiscardPrompt(false)}
        />
      ) : null}

      {noteOpenFor != null ? (
        <ToothNoteDialog
          fdi={noteOpenFor}
          initialText={noteText}
          onSave={onSaveNote}
          onDelete={onDeleteNote}
          onClose={() => setNoteOpenFor(null)}
        />
      ) : null}

      {readOnlyNoteFor != null ? (
        <ToothNoteDialog
          fdi={readOnlyNoteFor}
          initialText={readOnlyNoteText}
          readOnly
          onClose={() => setReadOnlyNoteFor(null)}
        />
      ) : null}

      {interceptNoteOpenFor != null ? (
        <ToothNoteDialog
          fdi={interceptNoteOpenFor}
          initialText={interceptNoteTextByTooth?.[String(interceptNoteOpenFor)] || ""}
          onSave={onSaveInterceptNote}
          onDelete={onDeleteInterceptNote}
          onClose={() => setInterceptNoteOpenFor(null)}
        />
      ) : null}

      {addCustomOpen ? (
        <CustomFindingDialog
          busy={busy.finding}
          onSave={onSaveCustomFinding}
          onClose={() => setAddCustomOpen(false)}
        />
      ) : null}

      {/* Mobile palette sheet — mounted whenever a draft is open. The
          sheet itself returns null when !open, so this is cheap. The
          chip that opens it is hidden once the wide desktop rail docks, so
          the user can't open the sheet anyway. Keeps state stable across resize. */}
      {showingDraft && !inIntercept ? (
        <MobilePaletteSheet
          open={mobilePaletteOpen}
          onClose={() => setMobilePaletteOpen(false)}
          findings={chartFindings}
          active={active}
          disabled={!canWrite}
          onPickFinding={(code) =>
            setActive((a) => (tutorialDemoMode || a?.kind !== "finding" || a.code !== code) ? { kind: "finding", code } : null)}
          onPickEraser={() =>
            setActive((a) => (a?.kind === "erase") ? null : { kind: "erase" })}
          onClear={() => setActive(null)}
          onOpenAddCustom={() => setAddCustomOpen(true)}
          canManageFindings={canManageFindings}
        />
      ) : null}

      {paletteOverride && inIntercept ? (
        <MobileProcedurePaletteSheet
          open={mobilePaletteOpen}
          onClose={() => setMobilePaletteOpen(false)}
          mode={chartModeProp}
        >
          {mobileProcedurePalette}
        </MobileProcedurePaletteSheet>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Floating palette chip — shown on mobile/tablet when a draft is
// open. Tapping it opens the bottom sheet. When a finding is armed
// the chip mirrors that finding's color + label so the dentist always
// sees what's "loaded" without opening the sheet.
// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// ChartModeToggle — V1-style top-level mode pill: [ Charting | Tx Plan ]
// ---------------------------------------------------------------------
// Cameron's locked decision: the dentist should be able to flip from
// charting findings into Tx Planning without leaving the Charting tab.
// In Tx Plan mode the whole TreatmentTab content mounts inline. Note +
// Eraser tools live inside their respective palette/per-tooth UIs in
// each mode — they're sub-tools, not separate top-level modes.
function ChartModeToggle({ mode, onChange }) {
  const items = [
    { value: "charting", label: "Charting", icon: Stethoscope },
    { value: "tx-plan",  label: "Tx Plan",  icon: ClipboardList },
  ];
  return (
    <div className="bg-white rounded-2xl shadow-sm p-2 sm:p-3" data-testid="chart-mode-toggle">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
        {items.map((it) => {
          const Icon = it.icon;
          const active = mode === it.value;
          return (
            <button
              key={it.value}
              type="button"
              onClick={() => onChange(it.value)}
              data-testid={`chart-mode-${it.value}`}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 ${
                active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ChairsideModeToggle lives in its own file so Doctor view and
// ChartingTab share the same four-mode control while keeping one
// anchored chart surface.

function FloatingPaletteChip({ active, findings, onClick }) {
  const armedCode = active?.kind === "finding" ? active.code : null;
  const eraserArmed = active?.kind === "erase";
  const armedFinding = armedCode
    ? (findings || []).find((f) => f.code === armedCode)
    : null;

  let body;
  if (eraserArmed) {
    body = (
      <>
        <span className="w-2.5 h-2.5 rounded-full bg-rose-500 ring-1 ring-rose-200" />
        <span>Eraser</span>
      </>
    );
  } else if (armedFinding) {
    body = (
      <>
        <span
          className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10"
          style={{
            backgroundColor: armedFinding.color_hex,
            border: armedFinding.is_pulp_involvement ? `1.5px solid ${PULP_OUTLINE_COLOR}` : "none",
          }}
        />
        <span className="truncate max-w-[10ch]">{armedFinding.display_name}</span>
      </>
    );
  } else {
    body = (
      <>
        <Palette className="w-4 h-4" />
        <span>Findings</span>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="floating-palette-chip"
      className={FLOATING_PALETTE_CHIP_CLASSNAME}
    >
      {body}
    </button>
  );
}

function FloatingProcedurePaletteChip({
  mode,
  armedProcedureName,
  armedProcedureColor,
  onClick,
}) {
  const modeLabel = mode === "future_need" ? "Future Need" : mode === "tx_plan" ? "Tx Plan" : "Today";
  const label = armedProcedureName || "Choose procedure";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="mobile-procedure-palette-chip"
      aria-label={armedProcedureName
        ? `Change procedure. ${armedProcedureName} selected for ${modeLabel}`
        : `Choose a procedure for ${modeLabel}`}
      className="fixed right-4 z-30 inline-flex min-h-12 max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-2xl transition-transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 min-[900px]:hidden"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
    >
      {armedProcedureName ? (
        <span
          className="h-3 w-3 shrink-0 rounded-full ring-1 ring-white/40"
          style={{ backgroundColor: armedProcedureColor || "#94a3b8" }}
          aria-hidden="true"
        />
      ) : (
        <Palette className="h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------
// ---------------------------------------------------------------------
// Undo / Redo paired buttons
// ---------------------------------------------------------------------
function UndoRedoButtons({ undoCount, redoCount, onUndo, onRedo }) {
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const undoTip = `Undo last action (${isMac ? "⌘Z" : "Ctrl+Z"})`;
  const redoTip = `Redo (${isMac ? "⌘⇧Z" : "Ctrl+Y"})`;
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="charting-undo-redo">
      <button
        type="button"
        onClick={onUndo}
        disabled={undoCount === 0}
        data-testid="charting-undo"
        title={undoTip}
        className="min-h-11 min-w-11 border-r border-slate-200 px-2.5 py-1.5 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 min-[900px]:min-h-0 min-[900px]:min-w-0"
      >
        <Undo2 className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={onRedo}
        disabled={redoCount === 0}
        data-testid="charting-redo"
        title={redoTip}
        className="min-h-11 min-w-11 px-2.5 py-1.5 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 min-[900px]:min-h-0 min-[900px]:min-w-0"
      >
        <Redo2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Erase toggle — chairside intercept-mode only. When on, surface
// clicks remove the painted item at that surface (Today draft,
// Tx Plan plan item, or Open future need depending on mode).
// Whole-tooth paints (surface=null) erase whole-tooth on any click.
function EraseToggle({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid="erase-toggle"
      title={on ? "Stop erasing" : "Click a surface to erase the paint there"}
      className={`inline-flex min-h-11 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-1 min-[900px]:min-h-0 ${
        on
          ? "border-rose-400 bg-rose-50 text-rose-700"
          : "border-slate-200 bg-white text-slate-600 hover:border-rose-300 hover:text-rose-600"
      }`}
    >
      <Eraser className="w-3.5 h-3.5" />
      Erase
    </button>
  );
}

// ---------------------------------------------------------------------
// Side toggle (mobile only) — Right / Left
// ---------------------------------------------------------------------
// Cuts the chart to half the mouth so 8 teeth × 2 rows fit comfortably
// on a phone. Hidden on desktop, where the full arch fits.
function SideToggle({ side, onChange }) {
  const items = [
    { value: "right", label: "Right" },
    { value: "left",  label: "Left" },
  ];
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="side-toggle">
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          onClick={() => onChange(it.value)}
          data-testid={`side-${it.value}`}
          data-tutorial-id={`charting-side-${it.value}`}
          className={`min-h-11 rounded-md px-3.5 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 min-[900px]:min-h-0 ${
            side === it.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Dentition mode toggle (Permanent / Both / Primary)
// ---------------------------------------------------------------------
function DentitionToggle({ mode, onChange }) {
  const items = [
    { value: "permanent", label: "Permanent" },
    { value: "both",      label: "Both" },
    { value: "primary",   label: "Primary" },
  ];
  return (
    <div
      className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5"
      data-testid="dentition-toggle"
      data-tutorial-id="charting-dentition-toggle"
    >
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          onClick={() => onChange(it.value)}
          data-testid={`dentition-${it.value}`}
          className={`min-h-11 rounded-md px-3.5 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1 min-[900px]:min-h-0 ${
            mode === it.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Empty state (no draft, no snapshot)
// ---------------------------------------------------------------------
function EmptyState() {
  return (
    <div className="text-center p-8" data-testid="charting-empty">
      <div className="w-12 h-12 rounded-full bg-teal-50 mx-auto flex items-center justify-center">
        <Smile className="w-5 h-5 text-teal-600" strokeWidth={2} />
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-900">No chart yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Press <span className="font-semibold">Start charting</span> to begin a new session.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// Start choice prompt — Transfer previous / Start fresh
// ---------------------------------------------------------------------
function StartChoiceDialog({ onTransfer, onFresh, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true"
      data-testid="charting-start-choice"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-bold text-slate-900">Edit chart</h3>
        <p className="text-sm text-slate-500">
          The patient has a finalized chart. Open it as a new draft to make changes,
          or start fresh with a clean canvas. Either way, finalizing creates a new
          version — the previous one stays in history.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
          <button
            type="button"
            onClick={onTransfer}
            data-testid="charting-start-transfer"
            className="px-4 py-3 rounded-xl border border-teal-300 bg-teal-50 text-left hover:border-teal-500"
          >
            <p className="text-sm font-semibold text-slate-900">Edit existing</p>
            <p className="text-xs text-slate-500 mt-0.5">Open the latest chart so you can modify it.</p>
          </button>
          <button
            type="button"
            onClick={onFresh}
            data-testid="charting-start-fresh"
            className="px-4 py-3 rounded-xl border border-slate-200 bg-white text-left hover:border-teal-400"
          >
            <p className="text-sm font-semibold text-slate-900">Start fresh</p>
            <p className="text-xs text-slate-500 mt-0.5">Blank canvas.</p>
          </button>
        </div>
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Finalize prompt — optional reason + Confirm Done charting
// ---------------------------------------------------------------------
function FinalizePrompt({ reason, onChange, busy, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true"
      data-testid="charting-finalize-prompt"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-bold text-slate-900">Finalize this chart?</h3>
        <p className="text-sm text-slate-500">
          A new dated snapshot will be created. Past snapshots remain unchanged.
          Optionally, capture why you're finalizing now.
        </p>
        <textarea
          value={reason}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Reason (optional)"
          data-testid="charting-finalize-reason"
          className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="charting-finalize-confirm"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {busy ? "Finalizing…" : "Confirm Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DiscardPrompt({ busy, onConfirm, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true"
      data-testid="charting-discard-prompt"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Discard this draft?</h3>
            <p className="text-sm text-slate-500 mt-1">
              All unsaved findings on the live draft will be lost. Past snapshots are not affected.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
          >
            Keep editing
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="charting-discard-confirm"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XIcon className="w-3.5 h-3.5" />}
            {busy ? "Discarding…" : "Discard draft"}
          </button>
        </div>
      </div>
    </div>
  );
}
